/// <amd-module name="Test/ManualTestReports/TestReportsCommon/FilterHelper" />
import {
    TestPointFieldNames,
    TestProgressFilterFields,
    TestPointHistorySnapshotFieldNames
} from "Test/ManualTestReports/TestReportsCommon/Constants";
import { ITestProgressReportFilterState } from "Test/ManualTestReports/TestReportsCommon/Contracts";
import { IFilterState } from "VSSUI/Utilities/Filter";
import { DateSKParser } from "Analytics/Shared/Utils/DateSKParser";
import * as Resources from "Test/ManualTestReports/TestReportsCommon/Resources";
import { OutcomeConstants } from "Test/ManualTestReports/TestReportsCommon/Constants";

export class FilterHelper {
    public static getFilterString(
        currentFilterState: ITestProgressReportFilterState,
        inputFilterString?: string,
        outcomeFilterFieldName?: string
    ): string {
        let planFilter = this.getFilterSubTextForSelectedPlans(currentFilterState.selectedPlanIds);
        let suiteFilter = this._getFilterSubTextForSelectedSuites(currentFilterState.selectedSuiteIds);
        let priorityFilter = this._getFilterSubTextForSelectedPriorityValues(currentFilterState.selectedPriorityValues);
        let configurationFilter = this._getFilterSubTextForSelectedConfigurations(currentFilterState.selectedConfigurations);
        let assignedToFilter = this._getFilterSubTextForSelectedAssignedTo(currentFilterState.selectedAssignedToValues);
        let testerToFilter = this._getFilterSubTextForSelectedTester(currentFilterState.selectedTesterValues);
        let dateFilter = this._getFilterSubTextForSelectedDateRange(currentFilterState.selectedDateRange);
        let outcomeFilter = this._getFilterSubTextForOutcome(currentFilterState.selectedOutcomeValues, outcomeFilterFieldName);

        let filterStr = "";
        let filterSubStrings: string[] = [];

        if (planFilter) {
            filterSubStrings.push(planFilter);
        }
        if(suiteFilter) {
            filterSubStrings.push(suiteFilter);
        }
        if (priorityFilter) {
            filterSubStrings.push(priorityFilter);
        }
        if (configurationFilter) {
            filterSubStrings.push(configurationFilter);
        }
        if (assignedToFilter) {
            filterSubStrings.push(assignedToFilter);
        }
        if (testerToFilter) {
            filterSubStrings.push(testerToFilter);
        }
                
        if (outcomeFilter) {
            filterSubStrings.push(outcomeFilter);
        }

        if (inputFilterString) {
            filterSubStrings.push(inputFilterString);
        }
        if (dateFilter) {
            filterSubStrings.push(dateFilter);
        }

        filterSubStrings = filterSubStrings.map(filterString => `( ${filterString} )`);

        filterStr = filterSubStrings.join(" and ");
        if (filterStr) {
            filterStr = `filter(${filterStr})`;
        }

        return filterStr;
    }

    public static getTestProgressFilterState(filterState: IFilterState): ITestProgressReportFilterState {
        let testPlanFilterValue: Array<string> = [];
        let testSuiteFilterValue: Array<string> = [];
        let testConfigurationsValue: Array<string> = [];
        let testPrioritiesValue: Array<string> = [];
        let assignedToValues: Array<string> = [];
        let testerValues: Array<string> = [];
        let outcomeValues: Array<string> = [];

        if (filterState) {
            testPlanFilterValue = filterState[TestProgressFilterFields.TestPlan]
                ? (filterState[TestProgressFilterFields.TestPlan]!.value as Array<string>)
                : [];
            testConfigurationsValue = filterState[TestProgressFilterFields.Configuration]
                ? (filterState[TestProgressFilterFields.Configuration]!.value as Array<string>)
                : [];
            testPrioritiesValue = filterState[TestProgressFilterFields.Priority]
                ? (filterState[TestProgressFilterFields.Priority]!.value as Array<string>)
                : [];
            assignedToValues = filterState[TestProgressFilterFields.AssignedTo]
                ? (filterState[TestProgressFilterFields.AssignedTo]!.value as Array<string>)
                : [];
            testerValues = filterState[TestProgressFilterFields.Tester] ? (filterState[TestProgressFilterFields.Tester]!.value as Array<string>) : [];
            outcomeValues = filterState[TestProgressFilterFields.Outcome]
                ? (filterState[TestProgressFilterFields.Outcome]!.value as Array<string>)
                : [];
            testSuiteFilterValue = filterState[TestProgressFilterFields.TestSuite]
                ? (filterState[TestProgressFilterFields.TestSuite]!.value as Array<string>)
                : [];
        }
        return {
            selectedPlanIds: testPlanFilterValue.map(testPlan => parseInt(testPlan)),
            selectedSuiteIds: testSuiteFilterValue.map(testSuite => parseInt(testSuite)),
            selectedConfigurations: testConfigurationsValue.map(testConfiguration => parseInt(testConfiguration)),
            selectedPriorityValues: testPrioritiesValue.map(testPriority => parseInt(testPriority)),
            selectedAssignedToValues: assignedToValues,
            selectedTesterValues: testerValues,
            selectedOutcomeValues: outcomeValues
        };
    }

    public static getTestPlanFilterValue(filterState?: IFilterState): Array<string> {
        if (!filterState) {
            return [];
        }
        return filterState[TestProgressFilterFields.TestPlan] ? (filterState[TestProgressFilterFields.TestPlan]!.value as Array<string>) : [];
    }

    public static getTestSuiteFilterValue(filterState?: IFilterState): Array<string> {
        if (!filterState) {
            return [];
        }
        return filterState[TestProgressFilterFields.TestSuite] ? (filterState[TestProgressFilterFields.TestSuite]!.value as Array<string>) : [];
    }

    private static _getFilterSubTextForSelectedValues(values: any[], fieldName: string): string {
        let filterText: string = "";

        let filterStrings: string[] = [];

        values.forEach((value: any) => {
            filterStrings.push(`${fieldName} eq ${value}`);
        });

        return filterStrings.join(" or ");
    }

    private static _getFilterTextForOutcomeValues(values: string[], fieldName: string): string {
        let filterStrings: string[] = [];

        let otherOutcomePresent: boolean = false;

        values.forEach((value: any) => {
            let valueText: string = "";
            if (value === OutcomeConstants.Other) {
                otherOutcomePresent = true;
            } else {
                valueText = value;
                filterStrings.push(`${fieldName} eq '${valueText}'`);
            }
        });
        if (!otherOutcomePresent) {
            return filterStrings.join(" or ");
        } else {
            let filterString1 = filterStrings.join(" or ");
            let filterString2 = `(${fieldName} ne '${OutcomeConstants.Passed}') and 
                                 (${fieldName} ne '${OutcomeConstants.Failed}') and 
                                 (${fieldName} ne '${OutcomeConstants.Blocked}') and 
                                 (${fieldName} ne '${OutcomeConstants.NotApplicable}') and 
                                 (${fieldName} ne '${OutcomeConstants.NotRun}')`;
            if (filterString1) {
                return `(${filterString1}) or ${filterString2}`;
            } else {
                return filterString2;
            }
        }
    }

    public static getFilterSubTextForSelectedPlans(planIds: number[]): string {
        return FilterHelper._getFilterSubTextForSelectedValues(planIds, TestPointFieldNames.PlanId);
    }

    public static _getFilterSubTextForSelectedSuites(suiteIds: number[]): string {
        return FilterHelper._getFilterSubTextForSelectedValues(suiteIds, TestPointFieldNames.SuiteId);
    }

    private static _getFilterSubTextForSelectedPriorityValues(priorityValues: number[]): string {
        return FilterHelper._getFilterSubTextForSelectedValues(priorityValues, TestPointFieldNames.Priority);
    }

    private static _getFilterSubTextForSelectedConfigurations(configurationValues: number[]): string {
        return FilterHelper._getFilterSubTextForSelectedValues(configurationValues, TestPointFieldNames.ConfigurationId);
    }

    private static _getFilterSubTextForSelectedAssignedTo(assignedToValues: string[]): string {
        return FilterHelper._getFilterSubTextForSelectedValues(assignedToValues, TestPointFieldNames.AssignedTo_UserSK.replace("/", ""));
    }

    private static _getFilterSubTextForSelectedTester(testerValues: string[]): string {
        return FilterHelper._getFilterSubTextForSelectedValues(testerValues, TestPointFieldNames.Tester_UserSK.replace("/", ""));
    }

    private static _getFilterSubTextForOutcome(outcomeValues: string[], outcomeFilterFieldName?: string): string {
        return FilterHelper._getFilterTextForOutcomeValues(
            outcomeValues,
            outcomeFilterFieldName ? outcomeFilterFieldName : TestPointFieldNames.Outcome
        );
    }

    private static _getFilterSubTextForSelectedDateRange(dateRange?: { fromDate: Date; toDate: Date }): string {
        let filterText: string = "";
        if (dateRange) {
            filterText = `${TestPointHistorySnapshotFieldNames.DateSK} ge ${this._getDateSKFromDate(dateRange.fromDate)} and ${
                TestPointHistorySnapshotFieldNames.DateSK
                } le ${this._getDateSKFromDate(dateRange.toDate)}`;
        }
        return filterText;
    }

    private static _getDateSKFromDate(date: Date): number {
        return DateSKParser.dateStringToDateSK(DateSKParser.convertDateToDateString(date));
    }
}
